import useTooltipStyles from '@components/v2/ui/Tooltip/tooltip.styles';
import Typography from '@components/v2/ui/Typography/Typography';
import { useTheme } from '@mui/material';
import { debounce } from 'lodash';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDynamicTagStyles } from './DynamicTags.styles';
import { DynamicTagsProps, TagDynamicProps, VisibleTag } from './DynamicTags.types';
import DynamicTagWithTooltip from './components/DynamicTagWithTooltip';
import { useMeasureTextWidthToTag } from './hooks/useMeasureTextWidthToTag';

const TAG_GAP = 4;

const DynamicTags = ({
  tags,
  variant,
  debounceDelay = 200,
  horizontalAlignment = 'left',
}: DynamicTagsProps) => {
  const theme = useTheme();
  const tagFontStyle = { fontWeight: theme.typography.body2.fontWeight };
  const { classes: tooltipClasses } = useTooltipStyles();
  const { classes } = useDynamicTagStyles({
    tagGap: TAG_GAP,
    horizontalAlignment,
    tooltipContainerClassName: tooltipClasses.tooltipContainer,
  });
  const [visibleTags, setVisibleTags] = useState<VisibleTag[]>([]);
  const [hiddenTags, setHiddenTags] = useState<TagDynamicProps[]>([]);
  const { boxDynamicContainerRef, calculateLayoutAndReturnVisibleAndHiddenTags } =
    useMeasureTextWidthToTag();

  const tagsContentKey = tags
    .map((tag) => `${tag.id}:${tag.label}:${tag.iconProps?.width ?? 0}`)
    .join('|');

  const calculateLayout = useCallback(() => {
    const layout = calculateLayoutAndReturnVisibleAndHiddenTags(tags, TAG_GAP);

    if (!layout) {
      return;
    }

    setHiddenTags(layout.hiddenTags);
    setVisibleTags(layout.visibleTags);
  }, [calculateLayoutAndReturnVisibleAndHiddenTags, tags]);

  const calculateLayoutRef = useRef(calculateLayout);
  const measuredTagsContentKeyRef = useRef(tagsContentKey);

  useEffect(() => {
    calculateLayoutRef.current = calculateLayout;
  }, [calculateLayout]);

  useEffect(() => {
    if (measuredTagsContentKeyRef.current === tagsContentKey) {
      return;
    }

    measuredTagsContentKeyRef.current = tagsContentKey;
    calculateLayoutRef.current();
  }, [tagsContentKey]);

  useEffect(() => {
    const boxDynamicContainer = boxDynamicContainerRef.current;

    if (!boxDynamicContainer) {
      return;
    }

    const runCalculateLayout = () => calculateLayoutRef.current();

    runCalculateLayout();

    if (document.fonts?.status === 'loading') {
      document.fonts.ready.then(runCalculateLayout);
    }

    const calculateLayoutDebounced = debounce(runCalculateLayout, debounceDelay);

    const observer = new ResizeObserver(calculateLayoutDebounced);

    observer.observe(boxDynamicContainer);

    return () => {
      observer.disconnect();
      calculateLayoutDebounced.cancel();
    };
  }, [debounceDelay]);

  return (
    <div ref={boxDynamicContainerRef} className={classes.boxDynamicContainer}>
      {visibleTags.map((tag) => (
        <DynamicTagWithTooltip
          key={tag.id}
          variant={variant}
          label={tag.label}
          icon={tag.iconProps?.icon}
          style={{
            ...tagFontStyle,
            ...(tag.hasEllipsis && { maxWidth: `${tag.maxWidth}px` }),
            ...tag.style,
          }}
          tooltipTitle={
            tag.description && (
              <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                {tag.description}
              </Typography>
            )
          }
        />
      ))}

      {hiddenTags.length > 0 && (
        <DynamicTagWithTooltip
          label={`+${hiddenTags.length}`}
          variant={variant}
          style={tagFontStyle}
          tooltipTitle={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {hiddenTags.map((tag) => (
                <span key={`${tag.label}-${tag.id}`}>{tag.label}</span>
              ))}
            </div>
          }
        />
      )}
    </div>
  );
};

export default memo(DynamicTags);
